import type { Locale } from '../i18n'
import { siteConfig } from '../siteConfig'
import { ATLAS_VIEWS, type AtlasView, type GeographyLevel } from '../types'

export const CLARITY_SCRIPT_ID = 'belgium-in-maps-clarity'
export const GA4_SCRIPT_ID = 'belgium-in-maps-ga4'
export const CLOUDFLARE_ANALYTICS_SCRIPT_ID = 'belgium-in-maps-cloudflare-analytics'

const LOCALES = new Set<Locale>(['en', 'fr', 'nl', 'de'])
const GEOGRAPHY_LEVELS = new Set<GeographyLevel>([
  'region', 'province', 'district', 'municipality', 'sector',
])
const ANALYTICAL_VIEWS = new Set<AtlasView>(ATLAS_VIEWS)
const PAGE_TYPES = new Set<AnalyticsPageType>([
  'atlas', 'municipality_profile', 'municipality_indicator', 'content',
])
const ROUTE_KINDS = new Set<AnalyticsRouteKind>(['atlas', 'semantic'])
const SAFE_IDENTIFIER = /^[a-z0-9][a-z0-9._:-]{0,79}$/i
const SAFE_PERIOD = /^[a-z0-9][a-z0-9 ._:/-]{0,31}$/i
const SAFE_AREA_CODE = /^[a-z0-9][a-z0-9._-]{0,31}$/i
const GA4_MEASUREMENT_ID = /^G-[A-Z0-9]{4,20}$/
const GA4_CAMPAIGN_SOURCES = new Set([
  'bing', 'facebook', 'google', 'horizon-mapper', 'linkedin', 'newsletter', 'partner', 'x',
])
const GA4_CAMPAIGN_MEDIA = new Set([
  'cpc', 'display', 'email', 'organic', 'partner', 'referral', 'social',
])
const SAFE_CAMPAIGN_ID = /^(?:horizon-mapper|launch|newsletter|organic|partner|search|social)(?:-[a-z0-9]{1,16})?$/

export type AnalyticsPageType = 'atlas' | 'municipality_profile' | 'municipality_indicator' | 'content'
export type AnalyticsRouteKind = 'atlas' | 'semantic'

/**
 * Deliberately small, contextual analytics schema. It has no free-form values,
 * form fields, search text, bearer tokens, subscriber state or user identity.
 */
export interface AnalyticsContext {
  locale?: Locale
  pageType?: AnalyticsPageType
  routeKind?: AnalyticsRouteKind
  topicId?: string
  metricId?: string
  measureId?: string
  geographyLevel?: GeographyLevel
  view?: AtlasView
  period?: string
  regionCode?: string
  provinceCode?: string
  municipalityCode?: string
  hasSelectedArea?: boolean
}

export const ANALYTICS_EVENT_NAMES = [
  'topic_change',
  'indicator_change',
  'measure_change',
  'period_change',
  'geography_change',
  'filter_change',
  'area_selection',
  'area_clear',
  'search_used',
  'trend_opened',
  'table_opened',
  'methodology_opened',
  'copy_link',
  'embed_code_copied',
  'share',
  'png_export',
  'language_change',
  'palette_change',
  'classification_change',
  'atlas_view_change',
  'newsletter_start',
  'newsletter_submission',
  'subscription_confirmation',
  'landing_atlas_open',
  'landing_discovery_open',
  'landing_municipality_open',
] as const

export type AnalyticsEventName = (typeof ANALYTICS_EVENT_NAMES)[number]

const ANALYTICS_EVENTS = new Set<AnalyticsEventName>(ANALYTICS_EVENT_NAMES)
const CLARITY_EVENTS: Partial<Record<AnalyticsEventName, string>> = {
  indicator_change: 'indicator_change',
  area_selection: 'area_selection',
  trend_opened: 'trend_open',
  table_opened: 'table_open',
  copy_link: 'share',
  embed_code_copied: 'embed_code_copied',
  share: 'share',
  png_export: 'png_export',
  newsletter_submission: 'newsletter_submit',
}

interface AnalyticsProviderOptions {
  suppressClarity?: boolean
  ga4MeasurementId?: string | null
}

interface AnalyticsPageView {
  path?: string
  context?: AnalyticsContext
}

declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[][] }
    dataLayer?: Array<IArguments | unknown[]>
    gtag?: (...args: unknown[]) => void
  }
}

let context: AnalyticsContext = {}
let providersEnabled = false
let clarityEnabled = false
let activeGa4MeasurementId: string | null = null
let pendingPageView: AnalyticsPageView | null = null
let lastPageViewKey = ''
let initialCampaign: SafeCampaign = {}

function safeIdentifier(value: unknown): string | undefined {
  return typeof value === 'string' && SAFE_IDENTIFIER.test(value) ? value : undefined
}

function safeAreaCode(value: unknown): string | undefined {
  return typeof value === 'string' && SAFE_AREA_CODE.test(value) ? value : undefined
}

/** Runtime sanitization remains strict even if an untyped caller bypasses TypeScript. */
export function sanitizeAnalyticsContext(candidate: unknown): AnalyticsContext {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return {}
  const source = candidate as Record<string, unknown>
  const sanitized: AnalyticsContext = {}
  if (LOCALES.has(source.locale as Locale)) sanitized.locale = source.locale as Locale
  if (PAGE_TYPES.has(source.pageType as AnalyticsPageType)) sanitized.pageType = source.pageType as AnalyticsPageType
  if (ROUTE_KINDS.has(source.routeKind as AnalyticsRouteKind)) sanitized.routeKind = source.routeKind as AnalyticsRouteKind
  if (GEOGRAPHY_LEVELS.has(source.geographyLevel as GeographyLevel)) {
    sanitized.geographyLevel = source.geographyLevel as GeographyLevel
  }
  if (ANALYTICAL_VIEWS.has(source.view as AtlasView)) sanitized.view = source.view as AtlasView
  const topicId = safeIdentifier(source.topicId)
  const metricId = safeIdentifier(source.metricId)
  const measureId = safeIdentifier(source.measureId)
  const regionCode = safeAreaCode(source.regionCode)
  const provinceCode = safeAreaCode(source.provinceCode)
  const municipalityCode = safeAreaCode(source.municipalityCode)
  if (topicId) sanitized.topicId = topicId
  if (metricId) sanitized.metricId = metricId
  if (measureId) sanitized.measureId = measureId
  if (typeof source.period === 'string' && SAFE_PERIOD.test(source.period)) sanitized.period = source.period
  if (regionCode) sanitized.regionCode = regionCode
  if (provinceCode) sanitized.provinceCode = provinceCode
  if (municipalityCode) sanitized.municipalityCode = municipalityCode
  if (typeof source.hasSelectedArea === 'boolean') sanitized.hasSelectedArea = source.hasSelectedArea
  return sanitized
}

function contextParams(value: AnalyticsContext): Record<string, string | boolean> {
  const result: Record<string, string | boolean> = {}
  for (const [key, item] of Object.entries(value)) {
    if (item !== undefined) result[key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)] = item
  }
  return result
}

function clarityCommand(...args: unknown[]): void {
  if (!window.clarity) {
    const queue = (...queuedArgs: unknown[]) => {
      queue.q = queue.q ?? []
      queue.q.push(queuedArgs)
    }
    queue.q = [] as unknown[][]
    window.clarity = queue
  }
  window.clarity(...args)
}

function googleCommand(..._args: unknown[]): void {
  window.dataLayer = window.dataLayer ?? []
  // gtag.js consumes the Arguments object produced by its documented shim.
  // A rest-parameter array can load the library without producing collection.
  window.dataLayer.push(arguments)
}

function configuredGa4MeasurementId(): string | null {
  return validGa4MeasurementId(siteConfig.ga4MeasurementId)
}

export function validGa4MeasurementId(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toUpperCase()
  return GA4_MEASUREMENT_ID.test(normalized) ? normalized : null
}

function enableClarity(): void {
  clarityCommand('consentv2', {
    ad_Storage: 'denied',
    analytics_Storage: 'granted',
  })
  if (!document.getElementById(CLARITY_SCRIPT_ID)) {
    const script = document.createElement('script')
    script.id = CLARITY_SCRIPT_ID
    script.async = true
    script.src = `https://www.clarity.ms/tag/${encodeURIComponent(siteConfig.clarityProjectId)}`
    document.head.append(script)
  }
  clarityEnabled = true
  sendClarityContext(context)
}

function enableGa4(measurementId: string): void {
  if (document.getElementById(GA4_SCRIPT_ID)) {
    activeGa4MeasurementId = measurementId
    return
  }
  // This queue is created only after explicit analytics consent. Advertising
  // storage, signals and personalisation remain denied/disabled.
  window.dataLayer = window.dataLayer ?? []
  window.gtag = googleCommand
  googleCommand('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
    wait_for_update: 500,
  })
  googleCommand('js', new Date())
  googleCommand('config', measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ads_data_redaction: true,
  })
  const script = document.createElement('script')
  script.id = GA4_SCRIPT_ID
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  document.head.append(script)
  activeGa4MeasurementId = measurementId
}

function sendClarityContext(value: AnalyticsContext): void {
  if (!clarityEnabled) return
  const tags: Partial<Record<keyof AnalyticsContext, string>> = {
    locale: value.locale,
    pageType: value.pageType,
    routeKind: value.routeKind,
    topicId: value.topicId,
    metricId: value.metricId,
    geographyLevel: value.geographyLevel,
  }
  for (const [key, item] of Object.entries(tags)) {
    if (item) clarityCommand('set', key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`), item)
  }
}

export function setAnalyticsContext(next: AnalyticsContext): void {
  context = sanitizeAnalyticsContext({ ...context, ...next })
  sendClarityContext(context)
}

/**
 * Returns a path only: query strings and fragments (including atlas state,
 * tokens, raw search values and campaign parameters) can never reach vendors.
 */
export function coarseAnalyticsPath(value: string): string {
  let pathname = '/'
  try {
    pathname = new URL(value, siteConfig.canonicalOrigin).pathname
  } catch {
    return '/'
  }
  const safeSegments = pathname.split('/').filter(Boolean).flatMap((segment) => {
    let decoded: string
    try {
      decoded = decodeURIComponent(segment)
    } catch {
      return []
    }
    return /^[a-z0-9][a-z0-9-]{0,79}$/i.test(decoded) ? [decoded.toLowerCase()] : []
  })
  return safeSegments.length ? `/${safeSegments.join('/')}/` : '/'
}

interface SafeCampaign {
  campaign_source?: string
  campaign_medium?: string
  campaign_id?: string
  campaign_name?: string
}

const CAMPAIGN_QUERY_KEYS = new Map([
  ['campaign_source', 'utm_source'],
  ['campaign_medium', 'utm_medium'],
  ['campaign_id', 'utm_id'],
  ['campaign_name', 'utm_campaign'],
] as const)

export function safeCampaignFromSearch(search: string): SafeCampaign {
  const params = new URLSearchParams(search)
  const source = params.get('utm_source')?.toLowerCase()
  const medium = params.get('utm_medium')?.toLowerCase()
  const campaignId = params.get('utm_id')?.toLowerCase()
  const campaignName = params.get('utm_campaign')?.toLowerCase()
  return {
    ...(source && GA4_CAMPAIGN_SOURCES.has(source) ? { campaign_source: source } : {}),
    ...(medium && GA4_CAMPAIGN_MEDIA.has(medium) ? { campaign_medium: medium } : {}),
    ...(campaignId && SAFE_CAMPAIGN_ID.test(campaignId) ? { campaign_id: campaignId } : {}),
    ...(campaignName && SAFE_CAMPAIGN_ID.test(campaignName) ? { campaign_name: campaignName } : {}),
  }
}

/** Keeps only normalized campaign parameters accepted by analytics. */
export function safeCampaignSearch(search: string): string {
  const campaign = safeCampaignFromSearch(search)
  const params = new URLSearchParams()
  for (const [campaignKey, queryKey] of CAMPAIGN_QUERY_KEYS) {
    const value = campaign[campaignKey]
    if (value) params.set(queryKey, value)
  }
  const serialized = params.toString()
  return serialized ? `?${serialized}` : ''
}

/**
 * Capture only the coarse, allowlisted entry attribution before atlas URL
 * reconciliation removes query parameters. The snapshot never contains raw
 * query text, arbitrary identifiers, or form/search state.
 */
export function captureInitialCampaign(search: string): SafeCampaign {
  initialCampaign = safeCampaignFromSearch(search)
  return { ...initialCampaign }
}

function dispatchPageView(pageView: AnalyticsPageView): void {
  if (!providersEnabled) return
  const merged = sanitizeAnalyticsContext({ ...context, ...pageView.context })
  const path = coarseAnalyticsPath(pageView.path ?? window.location.pathname)
  const key = JSON.stringify([path, merged.locale, merged.pageType, merged.routeKind])
  if (key === lastPageViewKey) return
  lastPageViewKey = key
  sendClarityContext(merged)
  if (activeGa4MeasurementId) {
    googleCommand('event', 'page_view', {
      ...contextParams(merged),
      ...initialCampaign,
      page_path: path,
      page_location: new URL(path, siteConfig.canonicalOrigin).toString(),
      send_to: activeGa4MeasurementId,
    })
  }
}

export function trackPageView(pageView: AnalyticsPageView = {}): void {
  pendingPageView = {
    path: pageView.path,
    context: sanitizeAnalyticsContext(pageView.context),
  }
  dispatchPageView(pendingPageView)
}

export function trackEvent(name: AnalyticsEventName, eventContext: AnalyticsContext = {}): void {
  // Runtime fail-closed check protects callers that bypass the TypeScript API.
  if (!providersEnabled || !ANALYTICS_EVENTS.has(name)) return
  const merged = sanitizeAnalyticsContext({ ...context, ...eventContext })
  const clarityEvent = CLARITY_EVENTS[name]
  if (clarityEnabled && clarityEvent) clarityCommand('event', clarityEvent)
  if (activeGa4MeasurementId) {
    googleCommand('event', name, {
      ...contextParams(merged),
      page_path: coarseAnalyticsPath(window.location.pathname),
      send_to: activeGa4MeasurementId,
    })
  }
}

export function enableAnalyticsProviders(options: AnalyticsProviderOptions = {}): void {
  providersEnabled = true
  if (!options.suppressClarity) enableClarity()
  const measurementId = validGa4MeasurementId(
    options.ga4MeasurementId === undefined ? configuredGa4MeasurementId() : options.ga4MeasurementId,
  )
  if (measurementId) enableGa4(measurementId)
  if (pendingPageView) dispatchPageView(pendingPageView)
}

export function denyAnalyticsProviders(): void {
  if (clarityEnabled || window.clarity) {
    window.clarity?.('consentv2', {
      ad_Storage: 'denied',
      analytics_Storage: 'denied',
    })
  }
  if (activeGa4MeasurementId || window.gtag) {
    googleCommand('consent', 'update', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    })
  }
  providersEnabled = false
}

/** Test isolation only; production code never needs to reset analytics state. */
export function resetAnalyticsForTests(): void {
  context = {}
  providersEnabled = false
  clarityEnabled = false
  activeGa4MeasurementId = null
  pendingPageView = null
  lastPageViewKey = ''
  initialCampaign = {}
}

/** Load the single cookie-free aggregate beacon after the critical render. */
export function enableAggregateAnalytics(): void {
  if (
    !siteConfig.cloudflareWebAnalyticsToken
    // A loopback preview cannot produce useful aggregate traffic and its CORS
    // rejection would turn otherwise-clean local Lighthouse runs red.
    || /^(?:127(?:\.\d{1,3}){3}|localhost|\[?::1\]?)$/i.test(window.location.hostname)
    || document.getElementById(CLOUDFLARE_ANALYTICS_SCRIPT_ID)
  ) return
  const script = document.createElement('script')
  script.id = CLOUDFLARE_ANALYTICS_SCRIPT_ID
  script.type = 'module'
  script.src = 'https://static.cloudflareinsights.com/beacon.min.js'
  script.dataset.cfBeacon = JSON.stringify({ token: siteConfig.cloudflareWebAnalyticsToken })
  document.head.append(script)
}
